import React, { useEffect, useState } from 'react';
import { Typography, Spin, Button, List, Popconfirm, theme } from 'antd';
import { message } from '../../../services/toastMessage';
import { IconBrandGoogle, IconDeviceDesktop } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import Settings2FA from '../../../components/TwoFactor/Settings2FA';
import ChangePasswordForm from '../../../components/ChangePasswordForm';
import SettingsCard from '../../../components/Settings/SettingsCard';
import {
  get2FAStatus,
  getActiveSessions,
  revokeSession,
  revokeAllOtherSessions,
  getLinkedOAuthProviders,
  unlinkOAuthProvider,
  IActiveSession,
  ILinkedProvider,
} from '../../../services/AuthenticationService';
import { useAppBreakpoint } from '../../../hooks/useAppBreakpoint';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';

const { Title, Text } = Typography;

const SecuritySection: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { isMobile } = useAppBreakpoint();

  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(true);

  const [providers, setProviders] = useState<ILinkedProvider[]>([]);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [unlinking, setUnlinking] = useState(false);

  const [sessions, setSessions] = useState<IActiveSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [revokingId, setRevokingId] = useState<number | null>(null);
  const [revokingAll, setRevokingAll] = useState(false);

  const load2FA = () => {
    setTwoFactorLoading(true);
    get2FAStatus()
      .then(setTwoFactorEnabled)
      .catch(() => message.error(translate(t, TRANSLATIONS.SETTINGS_2FA_LOAD_ERROR)))
      .finally(() => setTwoFactorLoading(false));
  };

  const loadProviders = () => {
    setProvidersLoading(true);
    getLinkedOAuthProviders()
      .then(setProviders)
      .catch(() => setProviders([]))
      .finally(() => setProvidersLoading(false));
  };

  const loadSessions = () => {
    setSessionsLoading(true);
    getActiveSessions()
      .then(setSessions)
      .catch(() => message.error(translate(t, TRANSLATIONS.SETTINGS_SESSIONS_LOAD_ERROR)))
      .finally(() => setSessionsLoading(false));
  };

  useEffect(() => {
    load2FA();
    loadProviders();
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isGoogleLinked = providers.some((p) => p.provider.toLowerCase() === 'google');

  const handleUnlinkGoogle = async () => {
    setUnlinking(true);
    try {
      await unlinkOAuthProvider('Google');
      message.success(translate(t, TRANSLATIONS.SETTINGS_OAUTH_UNLINK_SUCCESS));
      loadProviders();
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.SETTINGS_OAUTH_UNLINK_ERROR));
    } finally {
      setUnlinking(false);
    }
  };

  const handleRevokeSession = async (sessionId: number) => {
    setRevokingId(sessionId);
    try {
      await revokeSession(sessionId);
      message.success(translate(t, TRANSLATIONS.SETTINGS_SESSION_REVOKE_SUCCESS));
      loadSessions();
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.SETTINGS_SESSION_REVOKE_ERROR));
    } finally {
      setRevokingId(null);
    }
  };

  const handleRevokeAllOthers = async () => {
    setRevokingAll(true);
    try {
      await revokeAllOtherSessions();
      message.success(translate(t, TRANSLATIONS.SETTINGS_SESSION_REVOKE_ALL_SUCCESS));
      loadSessions();
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.SETTINGS_SESSION_REVOKE_ERROR));
    } finally {
      setRevokingAll(false);
    }
  };

  return (
    <div>
      {/* Redundant on mobile -- the list->detail header (Settings.tsx) already shows this section's name. */}
      {!isMobile && (
        <Title level={4} style={{ marginBottom: 20 }}>
          {translate(t, TRANSLATIONS.SETTINGS_NAV_SECURITY)}
        </Title>
      )}

      <SettingsCard title={translate(t, TRANSLATIONS.CHANGE_PASSWORD_TITLE)}>
        <ChangePasswordForm />
      </SettingsCard>

      <SettingsCard title={translate(t, TRANSLATIONS.SETTINGS_2FA_CARD_TITLE)}>
        {twoFactorLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin tip={translate(t, TRANSLATIONS.SETTINGS_2FA_LOADING)} />
          </div>
        ) : (
          <Settings2FA twoFactorEnabled={twoFactorEnabled} onUpdate={load2FA} />
        )}
      </SettingsCard>

      <SettingsCard title={translate(t, TRANSLATIONS.SETTINGS_OAUTH_CARD_TITLE)}>
        {providersLoading ? (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <Spin />
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 160 }}>
              <IconBrandGoogle size={20} />
              <div>
                <Text strong>Google</Text>
                <div>
                  <Text type="secondary">
                    {isGoogleLinked
                      ? translate(t, TRANSLATIONS.SETTINGS_OAUTH_CONNECTED)
                      : translate(t, TRANSLATIONS.SETTINGS_OAUTH_NOT_CONNECTED)}
                  </Text>
                </div>
              </div>
            </div>
            {isGoogleLinked && (
              <Popconfirm title={translate(t, TRANSLATIONS.SETTINGS_OAUTH_UNLINK_CONFIRM)} onConfirm={handleUnlinkGoogle}>
                <Button shape="round" danger loading={unlinking}>
                  {translate(t, TRANSLATIONS.SETTINGS_OAUTH_DISCONNECT_BUTTON)}
                </Button>
              </Popconfirm>
            )}
          </div>
        )}
      </SettingsCard>

      <SettingsCard
        title={translate(t, TRANSLATIONS.SETTINGS_SESSIONS_CARD_TITLE)}
        extra={
          sessions.length > 1 && (
            <Popconfirm title={translate(t, TRANSLATIONS.SETTINGS_SESSION_REVOKE_ALL_CONFIRM)} onConfirm={handleRevokeAllOthers}>
              <Button shape="round" size="small" danger loading={revokingAll}>
                {translate(t, TRANSLATIONS.SETTINGS_SESSION_REVOKE_ALL_BUTTON)}
              </Button>
            </Popconfirm>
          )
        }
        noMarginBottom
      >
        {sessionsLoading ? (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <Spin />
          </div>
        ) : (
          <List
            dataSource={sessions}
            renderItem={(session) => (
              <List.Item
                actions={
                  session.isCurrent
                    ? undefined
                    : [
                        <Button
                          key="revoke"
                          shape="round"
                          size="small"
                          danger
                          loading={revokingId === session.id}
                          onClick={() => handleRevokeSession(session.id)}
                        >
                          {translate(t, TRANSLATIONS.SETTINGS_SESSION_REVOKE_BUTTON)}
                        </Button>,
                      ]
                }
              >
                <List.Item.Meta
                  avatar={<IconDeviceDesktop size={22} color={token.colorTextSecondary} />}
                  title={
                    <span>
                      {session.deviceInfo || translate(t, TRANSLATIONS.SETTINGS_SESSION_UNKNOWN_DEVICE)}
                      {session.isCurrent && (
                        <Text type="success" style={{ marginLeft: 8, fontSize: 12 }}>
                          {translate(t, TRANSLATIONS.SETTINGS_SESSION_CURRENT_LABEL)}
                        </Text>
                      )}
                    </span>
                  }
                  description={
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {[session.location, session.ipAddress].filter(Boolean).join(' · ')}
                      {' · '}
                      {translate(t, TRANSLATIONS.SETTINGS_SESSION_LAST_ACTIVE_LABEL)}{' '}
                      {dayjs(session.lastUsedAt || session.createdAt).format('DD/MM/YYYY HH:mm')}
                    </Text>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </SettingsCard>
    </div>
  );
};

export default SecuritySection;
